import React, { useState } from 'react';
import HeroSection from '../components/books/HeroSection';
import BookCard from '../components/books/BookCard';
import OrderForm from '../components/books/OrderForm';
import UploadSection from '../components/books/UploadSection';
import FilterSection from '../components/books/FilterSection';
import { categories } from '../data/mockBooks';

/**
 * Home page component that displays the book catalog and ordering interface
 */
const HomePage = ({ 
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  searchBooks,
  selectedBook,
  setSelectedBook,
  favorites,
  toggleFavorite,
  uploadedImage,
  setUploadedImage,
  quantity,
  setQuantity,
  userInfo,
  setUserInfo,
  handleOrderSubmit,
  mockBooks
}) => {
  // Filter states
  const [priceRange, setPriceRange] = useState(null);
  const [sortBy, setSortBy] = useState('rating');
  const [ratingFilter, setRatingFilter] = useState(0);

  // Apply filters and sorting to books
  const filteredAndSortedBooks = searchBooks(searchQuery).filter(book => {
    // Apply price range filter
    if (priceRange) {
      if (!(book.price >= priceRange.min && book.price <= priceRange.max)) {
        return false;
      }
    }

    // Apply rating filter
    if (ratingFilter > 0 && book.rating < ratingFilter) {
      return false;
    }

    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'priceAsc':
        return a.price - b.price;
      case 'priceDesc':
        return b.price - a.price;
      case 'reviews':
        return b.reviews - a.reviews;
      case 'titleAsc':
        return a.title.localeCompare(b.title);
      case 'titleDesc':
        return b.title.localeCompare(a.title);
      default: // 'rating'
        return b.rating - a.rating;
    }
  });

  return (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
    <HeroSection searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
    
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        {categories.map(category => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
              selectedCategory === category
                ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-100 shadow-md'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      <div className="flex gap-8">
        {/* Filters Sidebar */}
        <div className="hidden lg:block w-80 flex-shrink-0">
          <div className="sticky top-8">
            <FilterSection
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              categories={categories}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              sortBy={sortBy}
              setSortBy={setSortBy}
              ratingFilter={ratingFilter}
              setRatingFilter={setRatingFilter}
              mockBooks={mockBooks}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Results Summary */}
          <div className="flex justify-between items-center mb-6">
            <p className="text-gray-600">
              Showing {filteredAndSortedBooks.length} books
              {selectedCategory !== 'All' && ` in ${selectedCategory}`}
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
            
            {/* Mobile Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="lg:hidden rounded-lg border-gray-300 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="rating">Highest Rated</option>
              <option value="priceAsc">Price: Low to High</option>
              <option value="priceDesc">Price: High to Low</option>
              <option value="reviews">Most Reviewed</option>
            </select>
          </div>

          {/* Book Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {filteredAndSortedBooks.map(book => (
              <BookCard 
                key={book.id} 
                book={book}
                isSelected={selectedBook?.id === book.id}
                onSelect={setSelectedBook}
                isFavorite={favorites.includes(book.id)}
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Order Form */}
      {(selectedBook || uploadedImage) && (
        <OrderForm
          selectedBook={selectedBook}
          uploadedImage={uploadedImage}
          quantity={quantity}
          setQuantity={setQuantity}
          userInfo={userInfo}
          setUserInfo={setUserInfo}
          handleOrderSubmit={handleOrderSubmit}
        />
      )}

      {/* Upload Section */}
      <UploadSection 
        uploadedImage={uploadedImage}
        setUploadedImage={setUploadedImage}
      />
    </div>
  </div>
  );
};

export default HomePage;
